import { getLogoutRedirectUrl } from '@white-label-helper/get-logout-redirect-url';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getCookieDomain } from '@white-label-helper/get-cookie-domain';
import { getUrlParam, isDedicatedFlow } from '@white-label-helper/dedicated-flow';
import { isBookingPortal } from '@white-label-helper/is-booking-portal';
import { COOKIES_KEY_PREFIX } from '../../constants';

import {
  handleRedirectCallback,
  clearStorageForInactiveSessionWrapper,
  createAuthClientWrapper,
  loginWrapper,
  logoutWrapper,
  checkSSOSessionWrapper,
  fetchUserWrapper,
  setUserTokenWrapper,
  getTokenWrapper,
  refreshUserWrapper
} from '../auth-flow-helpers';

// Types
import type { CustomAuth0Settings, CustomAuthFlowOptions } from '@white-label-types/auth';

/**
 * Auth plugin for Nuxt using Auth0.
 *
 * @param {Context} context
 */
const initAuthDefaultFlow = (
  {
    app,
    $config,
    route
  }: CustomAuthFlowOptions
) => {
  return {
    isAuthenticated: false,
    user: null,
    auth0Client: null,
    clientId: '',
    domain: '',
    audience: '',
    returnUrl: getLogoutRedirectUrl(), // store in the options and get as a cookie or localStorage

    /**
     * Initializes the Auth0 client and handles sessions.
     * @async
     * @returns {Promise<void>}
     */
    async initAuth() {
      // default values
      this.clientId = app['$partnerConfig']?.auth0_client_id || getAppVariable('auth0_client_id');
      this.domain = $config?.['AUTH0_DOMAIN'];
      this.audience = $config?.['AUTH0_AUDIENCE'];

      const options = {
        domain: this.domain,
        clientId: this.clientId,
        cacheLocation: 'localstorage' as const,
        cookieDomain: getCookieDomain(),
        authorizationParams: {
          redirect_uri: `${window.location.origin}/callback`,
          audience: this.audience,
          scope: 'openid profile email',
        },
        returnUrl: this.returnUrl,
      }

      if (isDedicatedFlow(route) || isBookingPortal) {
        // Set dedicated flow Auth0 client values if coming from Admin or Partner extranet's
        this.clientId = $config['AUTH0_PARTNER_CLIENT_ID'];
        this.domain = $config['AUTH0_PARTNER_DOMAIN'];
        this.audience = $config['AUTH0_PARTNER_AUDIENCE'];
        this.returnUrl = getUrlParam(route, 'returnUrl') || app['$cookies'].get(`${COOKIES_KEY_PREFIX}.${this.clientId}.returnUrl`);
        options.clientId = this.clientId;
        options.domain = this.domain;
        options.authorizationParams.audience = this.audience;
        options.returnUrl = this.returnUrl;
      }

      await createAuthClientWrapper(
        this,
        options
      );

      await handleRedirectCallback(app, this);
    },
    /**
     * Redirects the user to the Auth0 login page.
     * @async
     * @param {CustomAuth0Settings} customAuth0Settings - Custom brand settings for Auth0 login page
     * @returns {Promise<void>}
     */
    async login(customAuth0Settings: CustomAuth0Settings) {
      await loginWrapper(this.auth0Client, customAuth0Settings);
    },
    /**
     * Logs the user out and clears cookies.
     * @async
     * @returns {Promise<void>}
     */
    async logout() {
      if ($config['NUXT_ENV_IS_BOOKING_PORTAL'] || isDedicatedFlow(route)) {
        app['$cookies'].removeAll({ path: '/', domain: getCookieDomain() });
      }
      await logoutWrapper(this.auth0Client, this.returnUrl);
    },

    /**
     * Refreshes user information and authentication state.
     * @async
     * @returns {Promise<void>}
     */
    async refreshUser() {
     await refreshUserWrapper(this.auth0Client, this)
    },

    clearStorageForInactiveSession() {
      clearStorageForInactiveSessionWrapper(
        app, this
      );
      if (!$config['NUXT_ENV_IS_BOOKING_PORTAL']) window.location.href = '/';
    },
    /**
     * Checks the SSO session (dedicated flows e.g booking portal) and restores the user state if active.
     * @async
     * @returns {Promise<void>}
     */
    async checkSSOSession() {
      await checkSSOSessionWrapper(
        app,
        this
      );
    },

    /**
     * Fetches user details from Auth0's userinfo endpoint. Needed for SSO sessions
     * (dedicated flows e.g booking portal) to fetch user data manually
     *
     * @async
     * @param {string} token - The Auth0 access token.
     * @returns {Promise<void>}
     */
    async fetchUser(token: string) {
     await fetchUserWrapper(
        token,
        app,
        this
      )
    },

    /**
     * Sets tokens in localStorage and cookies for SSO session management
     * (dedicated flows e.g booking portal).
     * @param {string} token - The Auth0 access token.
     * @param {number} tokenExpiration - The token expiration.
     */
    setUserToken(token: string, tokenExpiration: number) {
      setUserTokenWrapper(
        token,
        tokenExpiration,
        app,
        this
      )
    },

    /**
     * Retrieves a token.
     * @async
     * @returns {Promise<string|null>} The token prefixed with "Bearer" or null if no token is available.
     */
    async getToken(): Promise<string | null> {
      return getTokenWrapper(this.auth0Client);
    },
  }
}

export { initAuthDefaultFlow }
